import { useState } from "react";
import { Link } from "react-router-dom";

import { api } from "@/lib/api";
import { googleAuthUrl } from "@/lib/googleAuth";
import type { AuthUser } from "@/types";
import { BrandLogo } from "@/components/shared/common/BrandLogo";
import { GoogleSignInButton } from "@/components/shared/common/GoogleSignInButton";
import baiviBanner from "@/assets/baivi/banner.png";

const WHITE_LABEL = import.meta.env.VITE_WHITE_LABEL as string | undefined;
const isWhiteLabel = !!WHITE_LABEL;

export function LoginPage({ onLogin }: { onLogin: (user: AuthUser, token: string) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { token, user } = await api.login(username, password);
      onLogin(user, token);
    } catch (err: any) {
      setError(err.message?.includes("401") ? "Invalid username or password" : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4 relative">
      {!isWhiteLabel && (
        <Link
          to="/"
          className="absolute top-6 left-6 flex items-center justify-center h-9 w-9 rounded-full bg-white shadow-card text-text-secondary hover:text-near-black transition-colors"
          title="Back to home"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
        </Link>
      )}
      <div className="w-full max-w-md rounded-card bg-white shadow-card overflow-hidden">
        {isWhiteLabel && (
          <img src={baiviBanner} alt={WHITE_LABEL} className="w-full object-cover" />
        )}
        <div className="p-6 sm:p-8">
        <div className="mb-6 flex justify-center">
          <BrandLogo imgClassName="h-16 w-auto" />
        </div>

        {!isWhiteLabel && (
          <>
            <GoogleSignInButton onClick={() => { window.location.href = googleAuthUrl(); }} />
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border-card" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs text-text-secondary">or continue with username</span>
              </div>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              autoFocus
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              required
            />
          </div>

          {error && (
            <p className="rounded-badge bg-rose-100 px-3 py-2 text-xs font-medium text-error-text">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-2 w-full rounded-btn bg-near-black px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs font-medium text-text-secondary">
          Powered by <span className="font-bold text-emerald-600">DitoSoft</span> AI
        </p>
        </div>
      </div>
    </div>
  );
}
